import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Card,
  CardContent,
  Grid,
  Divider,
  Alert,
} from '@mui/material';
import {
  Inventory as ContainerIcon,
  Close as CloseIcon,
  LocationOn as LocationIcon,
  Category as CategoryIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { ContainerForClassification } from '../../../../core/interfaces/container.interface';

interface ContainerInfoProps {
  container: ContainerForClassification;
  onClear: () => void;
}

export const ContainerInfo: React.FC<ContainerInfoProps> = ({
  container,
  onClear,
}) => {
  const [copySuccess, setCopySuccess] = useState<string | null>(null);

  const handleCopyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(`Código "${text}" copiado al portapapeles`);
      setTimeout(() => setCopySuccess(null), 3000);
    } catch (err) {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(`Código "${text}" copiado al portapapeles`);
      setTimeout(() => setCopySuccess(null), 3000);
    }
  };
  // Helper para traducir estados de contenedores de destino
  const translateDestinationContainerStatus = (statusName: string): string => {
    const statusTranslations: { [key: string]: string } = {
      'Open': 'Abierto',
      'Closed': 'Cerrado',
      'Paused': 'Pausado',
      'In Transit': 'En tránsito',
      'Loading': 'Cargando',
      'Unloading': 'Descargando',
      'Maintenance': 'Mantenimiento',
    };
    return statusTranslations[statusName] || statusName;
  };

  // Verificar si el contenedor está cerrado
  const isContainerClosed = (container: ContainerForClassification) => {
    // Usar el campo preloaded si está disponible, sino usar los campos legacy
    const statusName = container.container_status?.name || container.container_status_name;
    return container.is_closed || container.status === 'closed' || statusName === 'Closed';
  };

  // Obtener el color del estado
  const getStatusColor = () => {
    const statusName = container.container_status?.name || container.container_status_name;
    
    if (isContainerClosed(container)) {
      return 'error';
    }
    
    // Colores específicos para cada estado
    switch (statusName) {
      case 'Open':
        return 'success';
      case 'Paused':
        return 'warning';
      case 'In Transit':
        return 'info';
      case 'Loading':
        return 'primary';
      case 'Unloading':
        return 'secondary';
      case 'Maintenance':
        return 'default';
      default:
        return 'success';
    }
  };

  // Obtener el texto del estado
  const getStatusText = () => {
    if (isContainerClosed(container)) {
      return 'Cerrado';
    }
    const statusName = container.container_status?.name || container.container_status_name;
    return statusName ? translateDestinationContainerStatus(statusName) : 'Abierto';
  };

  return (
    <Card variant="outlined" sx={{ backgroundColor: 'background.paper' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <ContainerIcon color="primary" />
            <Typography variant="h6" fontWeight="medium">
              {container.code}
            </Typography>
            <IconButton
              size="small"
              onClick={() => handleCopyToClipboard(container.code)}
              sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main', backgroundColor: 'action.hover' } }}
              title="Copiar código al portapapeles"
            >
              <CopyIcon fontSize="small" />
            </IconButton>
          </Box>
          <IconButton
            onClick={onClear}
            size="small"
            color="error"
            aria-label="Limpiar selección"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          {/* Estado del contenedor */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Chip
                label={getStatusText()}
                color={getStatusColor()}
                size="small"
                icon={<ContainerIcon />}
              />
            </Box>
          </Grid>

          {/* Descripción */}
          {container.description && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {container.description}
              </Typography>
            </Grid>
          )}

          {/* Tipo de contenedor */}
          {(container.container_category?.name || container.container_type_name) && (
            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <CategoryIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  Tipo:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {container.container_category?.name || container.container_type_name}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Ubicación */}
          {(container.location?.name || container.location_name) && (
            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <LocationIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  Ubicación:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {container.location?.name || container.location_name}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Información adicional si está disponible */}
          {container.warehouse_name && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Almacén: {container.warehouse_name}
              </Typography>
            </Grid>
          )}

          {/* Alerta si el contenedor está cerrado */}
          {isContainerClosed(container) && (
            <Grid item xs={12}>
              <Chip
                label="Este contenedor está cerrado y no puede recibir artículos"
                color="warning"
                variant="outlined"
                size="small"
                sx={{ mt: 1 }}
              />
            </Grid>
          )}
        </Grid>

        {/* Alerta de copia exitosa */}
        {copySuccess && (
          <Alert severity="success" onClose={() => setCopySuccess(null)} sx={{ mt: 2 }}>
            {copySuccess}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};
